import React from 'react';
import { useEffect, useState } from 'react';
import { postData } from '../../Api';

/**
 * Binds an admin's own Bale (بله) chat to their panel account.
 *
 * The bot cannot know who is who, so the admin proves it: the panel issues a
 * one-time code, the admin sends that code to the bot from their own Bale, and
 * the backend looks for it among the bot's recent messages. That keeps the flow
 * correct even when several admins bind at the same time.
 */
export default function TaroffBaleConnect(props) {
    const [status, setStatus] = useState(null);
    const [code, setCode] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    const loadStatus = async () => {
        const response = await postData('/api/panel/page/taroff-tasks/bale-status');
        if (response.status === 'success') setStatus(response.data);
    };

    useEffect(() => { loadStatus(); }, []);

    const run = async (fn) => {
        setBusy(true);
        setError(null);
        setNotice(null);
        try { await fn(); } finally { setBusy(false); }
    };

    const handleGetCode = () => run(async () => {
        const response = await postData('/api/panel/page/taroff-tasks/bale-code');
        if (response.status === 'success') setCode(response.data);
        else setError(response.message || 'خطا در دریافت کد');
    });

    const handleBind = () => run(async () => {
        const response = await postData('/api/panel/page/taroff-tasks/bale-bind', { code: code?.code });
        if (response.status === 'success') {
            setCode(null);
            setNotice('اتصال با موفقیت انجام شد.');
            await loadStatus();
        } else {
            setError(response.message || 'اتصال ناموفق بود');
        }
    });

    const handleTest = () => run(async () => {
        const response = await postData('/api/panel/page/taroff-tasks/bale-test');
        if (response.status === 'success') setNotice('پیام آزمایشی ارسال شد — بله را ببینید.');
        else setError(response.message || 'ارسال ناموفق بود');
    });

    const handleUnbind = () => run(async () => {
        await postData('/api/panel/page/taroff-tasks/bale-unbind');
        setNotice('اتصال قطع شد.');
        await loadStatus();
    });

    const botName = status?.bot_username || code?.bot_username;

    return (
        <div dir="rtl" className="font-iransans text-gray-900 dark:text-gray-100">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold">
                    <i className="far fa-paper-plane ml-2 text-indigo-500" />
                    اطلاع‌رسانی در بله
                </h2>
                <button onClick={props.onDismiss} className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-700" title="بستن">
                    <i className="fas fa-xmark fa-lg" />
                </button>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 leading-7 mb-4">
                با اتصال حساب بله، هر بار کاری به شما محول شود یا روی کارهای شما پیام و تغییر وضعیتی ثبت شود،
                همان‌جا در بله خبردار می‌شوید.
            </p>

            {status?.connected ? (
                <div className="rounded-xl border border-green-200 dark:border-green-900 bg-green-50 dark:bg-green-950/40 p-4">
                    <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm font-medium">
                        <i className="fas fa-circle-check" />
                        حساب شما متصل است
                        <span className="font-mono text-xs text-green-600/70 dark:text-green-400/70" dir="ltr">({status.chat_id})</span>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <button onClick={handleTest} disabled={busy} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium disabled:opacity-40">
                            <i className={busy ? 'fas fa-spinner fa-spin' : 'fas fa-bell'} />
                            ارسال پیام آزمایشی
                        </button>
                        <button onClick={handleUnbind} disabled={busy} className="px-4 py-1.5 rounded-lg bg-gray-100 dark:bg-zinc-700 hover:bg-gray-200 dark:hover:bg-zinc-600 text-xs">
                            قطع اتصال
                        </button>
                    </div>
                </div>
            ) : (
                <ol className="space-y-3">
                    <li className="rounded-xl border border-gray-200 dark:border-zinc-700 p-4">
                        <div className="text-sm font-medium mb-2">۱. کد اتصال را بگیرید</div>
                        {code ? (
                            <div className="inline-flex items-center gap-2 rounded-lg bg-gray-900 dark:bg-white text-white dark:text-gray-900 px-4 py-2 font-mono text-lg tracking-widest" dir="ltr">
                                {code.code}
                            </div>
                        ) : (
                            <button onClick={handleGetCode} disabled={busy} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium disabled:opacity-40">
                                <i className={busy ? 'fas fa-spinner fa-spin' : 'fas fa-key'} />
                                دریافت کد
                            </button>
                        )}
                    </li>
                    <li className="rounded-xl border border-gray-200 dark:border-zinc-700 p-4">
                        <div className="text-sm font-medium mb-1">۲. کد را در بله برای ربات بفرستید</div>
                        <div className="text-xs text-gray-500 leading-6">
                            در پیام‌رسان بله ربات
                            {botName ? <span className="font-mono mx-1" dir="ltr">@{botName}</span> : ' پشتیبانی '}
                            را باز کنید و همین کد را برای آن ارسال کنید.
                        </div>
                    </li>
                    <li className="rounded-xl border border-gray-200 dark:border-zinc-700 p-4">
                        <div className="text-sm font-medium mb-2">۳. اتصال را تأیید کنید</div>
                        <button onClick={handleBind} disabled={busy || !code} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-medium disabled:opacity-40">
                            <i className={busy ? 'fas fa-spinner fa-spin' : 'fas fa-link'} />
                            بررسی و اتصال
                        </button>
                    </li>
                </ol>
            )}

            {error && <div className="mt-3 text-red-600 text-xs">{error}</div>}
            {notice && <div className="mt-3 text-green-600 text-xs">{notice}</div>}
        </div>
    );
}
